import { yupResolver } from '@hookform/resolvers/yup'
import { useAddressSliceActions } from 'feature/addresses/hooks/useAddressSliceActions'
import { useAddressState } from 'feature/addresses/hooks/useAddressState'
import { useUpdateCompanyProfileMutation } from 'feature/company/redux/company.api'
import { useGetUserQuery } from 'feature/user/redux/user.api'
import { useSetNotice } from 'hooks/useSetNotice'
import { useCallback, useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { Severity } from 'widget/Notifications'

import { CompanyEditFormValues } from './CompanyEditProfile'
import {
  companyEditFormSchema,
  companyEditFormDefaultValues,
} from './companyEditFormObjects'

export function useCompanyEditProfile() {
  const { isLoading, data } = useGetUserQuery()
  const [updateProfile, { isLoading: isUpdating, isError, error }] =
    useUpdateCompanyProfileMutation()
  const setNotice = useSetNotice()
  const navigate = useNavigate()

  // Юридический адрес — addressKey 'default'
  const legalAddressState = useAddressState('default')
  const legalActions = useAddressSliceActions('default')

  // Фактический адрес — addressKey 'company'
  const actualAddressState = useAddressState('company')
  const actualActions = useAddressSliceActions('company')

  const formMethods = useForm<CompanyEditFormValues>({
    defaultValues: useMemo(() => companyEditFormDefaultValues(data), [data]),
    resolver: yupResolver(companyEditFormSchema) as any,
    mode: 'onChange',
  })

  const {
    formState: { isValid: isValidForm, isDirty: isDirtyForm },
  } = formMethods

  // Чистим возможный stale state из sessionStorage / прошлых страниц
  useEffect(() => {
    legalActions.clearAddress()
    actualActions.clearAddress()
  }, [])

  useEffect(() => {
    if (data) {
      formMethods.reset(companyEditFormDefaultValues(data))
      if (data.legalAddress) {
        legalActions.setInputAddress(data.legalAddress.address)
        legalActions.setInputAddressSuggest(data.legalAddress)
        legalActions.setIsValidAddress(true)
      }
      if (data.actualAddress) {
        actualActions.setInputAddress(data.actualAddress.address)
        actualActions.setInputAddressSuggest(data.actualAddress)
        actualActions.setIsValidAddress(true)
      }
    }
  }, [data, formMethods])

  useEffect(() => {
    if (isError) {
      const errorText = (error as any)?.data?.message
      setNotice(errorText || 'Ошибка сохранения данных', Severity.error)
    }
  }, [isError])

  // Проверка dirty для юр. адреса
  useEffect(() => {
    if (legalAddressState.input === data?.legalAddress?.address) {
      legalActions.setIsDirtyAddress(false)
    }
  }, [legalAddressState.input, legalAddressState.inputSuggest])

  // Проверка dirty для факт. адреса
  useEffect(() => {
    if (actualAddressState.input === data?.actualAddress?.address) {
      actualActions.setIsDirtyAddress(false)
    }
  }, [actualAddressState.input, actualAddressState.inputSuggest])

  const onSubmit = useCallback(async () => {
    const values = formMethods.getValues()

    try {
      await updateProfile({
        firstName: values.firstName,
        lastName: values.lastName,
        fatherName: values.fatherName || null,
        customerAddress: actualAddressState.inputSuggest,
        companyName: values.companyName,
        kpp: values.kpp || null,
        ogrn: values.ogrn,
        legalAddress: legalAddressState.inputSuggest,
        actualAddress: actualAddressState.inputSuggest,
        directorFullName: values.directorFullName,
        directorPosition: values.directorPosition,
      }).unwrap()
      setNotice('Данные успешно обновлены', Severity.success)
      navigate('/settings')
    } catch {
      // ошибка обрабатывается в useEffect выше
    }
  }, [
    formMethods,
    updateProfile,
    legalAddressState.inputSuggest,
    actualAddressState.inputSuggest,
  ])

  const isDirty =
    isDirtyForm || legalAddressState.isDirty || actualAddressState.isDirty
  const isValid = isValidForm && legalAddressState.isValid

  return {
    formMethods,
    isLoading,
    isUpdating,
    isDirty,
    isValid,
    onSubmit,
    navigate,
  }
}
